import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import ApiService from "../../_services/ApiService";
import AuthenticationService from "../../_services/AuthenticationService";
import "./TDEBatchesList.css";

interface Batch {
  id: number;
  name: string;
  schoolId: number;
  schoolName: string;
  courseId: number;
  courseName: string;
}

const TDEBatchesList: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const history = useHistory();

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const currentUser = AuthenticationService.currentUserValue;
      const instructorId = currentUser?.instructorId;

      if (!instructorId) {
        setError("Instructor ID not found. Please log in again.");
        setLoading(false);
        return;
      }

      const response = await ApiService.syncBatches(instructorId);

      if (response.data.status === 1) {
        setBatches(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch batches");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching batches");
    } finally {
      setLoading(false);
    }
  };

  const handleBatchClick = (batchId: number) => {
    // Navigate to test selection page
    history.push(`/tde/batch/${batchId}/tests`);
  };

  const handleBack = () => {
    history.goBack();
  };

  if (loading) {
    return (
      <div className="tde-batches-container">
        <div className="loading">Loading batches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tde-batches-container">
        <div className="error-message">{error}</div>
        <button className="back-button" onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="tde-batches-container">
      <div className="header">
        <button className="back-button" onClick={handleBack}>
          <span aria-hidden="true">←</span> Back
        </button>
        <h1>
          Test Data Entry
          <span className="header-subtitle">Select a batch to enter or review marks</span>
        </h1>
      </div>

      <div className="batches-grid">
        {batches.length === 0 ? (
          <div className="no-batches">No batches found</div>
        ) : (
          batches.map((batch) => (
            <div
              key={batch.id}
              className="batch-card"
              onClick={() => handleBatchClick(batch.id)}
            >
              <div className="batch-card-header">
                <div className="batch-avatar" aria-hidden="true">
                  {(batch.name || "?").trim().charAt(0).toUpperCase()}
                </div>
                <div className="batch-name-wrap">
                  <h3 className="batch-name" title={batch.name}>{batch.name}</h3>
                  <span className="batch-id">ID {batch.id}</span>
                </div>
              </div>
              <div className="batch-card-body">
                <div className="batch-info">
                  <div className="info-item">
                    <span className="info-label">School</span>
                    <span
                      className={`info-value${batch.schoolName ? "" : " info-value-empty"}`}
                      title={batch.schoolName || ""}
                    >
                      {batch.schoolName || "—"}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Course</span>
                    <span
                      className={`info-value${batch.courseName ? "" : " info-value-empty"}`}
                      title={batch.courseName || ""}
                    >
                      {batch.courseName || "—"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="batch-card-footer">
                <button className="select-button">
                  Select Batch <span className="select-arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TDEBatchesList;
